/**
 * ActiveScopeProvider — contexto de tenant/BU ATIVOS da sessão (DD-004, Req 5/6).
 *
 * Diferente do escopo derivado direto do JWT (que é fixo enquanto o token não
 * rotaciona), este provider mantém a BU ATIVA escolhida pelo usuário no seletor
 * do Shell. Trocar a BU aqui re-chaveia as queries (via ScopedQueryProvider →
 * QueryProvider) e força refetch escopado — resolvendo o "nada acontece ao trocar
 * de BU".
 *
 * Inicialização:
 * - tenantId e a BU padrão vêm dos claims do JWT (tenant_id / business_unit_id).
 * - a BU ativa é persistida em sessionStorage (id não-sensível; expira com a aba).
 * - ao trocar de usuário (novo login), uma BU persistida que não pertence ao
 *   escopo (`bu_ids`) é descartada em favor da BU padrão do novo token.
 *
 * Fica ACIMA do ScopedQueryProvider e ABAIXO do AuthProvider na árvore (main.tsx).
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useAuth } from "../AuthProvider.js";
import { decodeTokenClaims } from "../../shared/auth/tokenClaims.js";

const ACTIVE_BU_KEY = "azim.activeBu";

export interface ActiveScopeValue {
  /** GUID do tenant corrente (do JWT). `null` antes do login. */
  readonly tenantId: string | null;
  /** GUID da BU ativa (escolhida no seletor ou a padrão do JWT). */
  readonly businessUnitId: string | null;
  /** GUIDs de todas as BUs no escopo do usuário (do claim `bu_ids`). */
  readonly buIds: readonly string[];
  /** Define a BU ativa e persiste a escolha. */
  setBusinessUnit(buId: string): void;
}

const ActiveScopeContext = createContext<ActiveScopeValue | null>(null);

export function useActiveScope(): ActiveScopeValue {
  const ctx = useContext(ActiveScopeContext);
  if (!ctx) {
    throw new Error("useActiveScope deve ser usado dentro de ActiveScopeProvider");
  }
  return ctx;
}

function readPersistedBu(): string | null {
  try {
    return sessionStorage.getItem(ACTIVE_BU_KEY);
  } catch {
    return null;
  }
}

export function ActiveScopeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const claims = useMemo(
    () => (user?.token ? decodeTokenClaims(user.token) : null),
    [user?.token],
  );
  const tenantId = claims?.tenant_id ?? null;
  const buIds = claims?.bu_ids ?? [];
  const defaultBu = claims?.business_unit_id ?? buIds[0] ?? null;

  const [businessUnitId, setBuState] = useState<string | null>(() => readPersistedBu());

  // Reconcilia a BU ativa com o escopo do token corrente: descarta uma BU
  // persistida que não pertence ao usuário atual (troca de login) e adota a
  // BU padrão do JWT quando não há seleção válida.
  useEffect(() => {
    if (!defaultBu) return;
    setBuState((cur) => {
      const inScope =
        !!cur && (cur === claims?.business_unit_id || buIds.includes(cur));
      const next = inScope ? cur : defaultBu;
      try {
        if (next) sessionStorage.setItem(ACTIVE_BU_KEY, next);
      } catch {
        /* sessionStorage indisponível — mantém apenas em memória */
      }
      return next;
    });
    // buIds derivado de claims; depender de defaultBu/claims cobre as mudanças.
  }, [defaultBu, claims, buIds]);

  const setBusinessUnit = useCallback((buId: string) => {
    setBuState(buId);
    try {
      sessionStorage.setItem(ACTIVE_BU_KEY, buId);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<ActiveScopeValue>(
    () => ({ tenantId, businessUnitId, buIds, setBusinessUnit }),
    [tenantId, businessUnitId, buIds, setBusinessUnit],
  );

  return <ActiveScopeContext.Provider value={value}>{children}</ActiveScopeContext.Provider>;
}
